import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Box,
  Typography,
  Alert,
  InputAdornment,
} from '@mui/material';
import { Add, Edit, Delete, Search } from '@mui/icons-material';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Manutencao, Veiculo } from '../types';
import { manutencaoService } from '../services/manutencaoService';
import { veiculoService } from '../services/veiculoService';
import { getErrorMessage } from '../utils/errors';
import ConfirmDialog from '../components/ConfirmDialog';
import DateMaskField from '../components/DateMaskField';

const schema = yup.object({
  veiculoId: yup.number().required('Veículo é obrigatório').min(1, 'Selecione um veículo'),
  tipo: yup.string().required('Tipo é obrigatório'),
  tipoServico: yup.string().required('Tipo de serviço é obrigatório'),
  dataManutencao: yup.string().required('Data é obrigatória'),
  descricao: yup.string().required('Descrição é obrigatória'),
  pecasSubstituidas: yup.string(),
  valor: yup.number().required('Valor é obrigatório').min(0),
  quilometragemNaManutencao: yup.number().required('Quilometragem é obrigatória').min(0),
  proximaTrocaData: yup.string(),
  proximaTrocaQuilometragem: yup.number(),
}).required();

const Manutencoes: React.FC = () => {
  const [manutencoes, setManutencoes] = useState<Manutencao[]>([]);
  const [veiculos, setVeiculos] = useState<Veiculo[]>([]);
  const [open, setOpen] = useState(false);
  const [editingManutencao, setEditingManutencao] = useState<Manutencao | null>(null);
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<{ open: boolean; id: number | null }>({ open: false, id: null });
  const [filtro, setFiltro] = useState('');

  const { register, control, handleSubmit, reset, formState: { errors } } = useForm({
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    loadManutencoes();
    loadVeiculos();
  }, []);

  const loadManutencoes = async () => {
    try {
      const data = await manutencaoService.listar();
      setManutencoes(data);
    } catch (error) {
      console.error('Erro ao carregar manutenções', error);
    }
  };

  const loadVeiculos = async () => {
    try {
      const data = await veiculoService.listar();
      setVeiculos(data);
    } catch (error) {
      console.error('Erro ao carregar veículos', error);
    }
  };

  const manutencaoVazio = () => ({ tipo: 'PREVENTIVA', tipoServico: 'TROCA_OLEO' });

  const handleOpen = (manutencao?: Manutencao) => {
    if (manutencao) {
      setEditingManutencao(manutencao);
      reset(manutencao);
    } else {
      setEditingManutencao(null);
      reset(manutencaoVazio() as any);
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingManutencao(null);
    reset(manutencaoVazio() as any);
    setErro('');
  };

  const onSubmit = async (data: any) => {
    setSalvando(true);
    try {
      if (editingManutencao?.id) {
        await manutencaoService.atualizar(editingManutencao.id, data);
      } else {
        await manutencaoService.cadastrar(data);
      }
      loadManutencoes();
      handleClose();
    } catch (error) {
      setErro(getErrorMessage(error));
    } finally {
      setSalvando(false);
    }
  };

  const handleDelete = (id: number) => {
    setConfirmDelete({ open: true, id });
  };

  const confirmDeleteAction = async () => {
    if (!confirmDelete.id) return;
    try {
      await manutencaoService.deletar(confirmDelete.id);
      loadManutencoes();
      setConfirmDelete({ open: false, id: null });
    } catch (error) {
      console.error('Erro ao deletar manutenção', error);
      setConfirmDelete({ open: false, id: null });
    }
  };

  const termo = filtro.trim().toLowerCase();
  const manutencoesFiltradas = termo
    ? manutencoes.filter((item) => Object.values(item).filter(Boolean).some((campo) => String(campo).toLowerCase().includes(termo)))
    : manutencoes;

  return (
    <Container disableGutters maxWidth={false}>
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h4">Manutenções</Typography>
        <Box display="flex" gap={2} alignItems="center" sx={{ flexWrap: 'wrap' }}>
          <TextField
            size="small"
            label="Pesquisar"
            value={filtro}
            onChange={(e) => setFiltro(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }}
            sx={{ minWidth: 260 }}
          />
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpen()}
          >
            Nova Manutenção
          </Button>
        </Box>
      </Box>

      {erro && <Alert severity="error" sx={{ mb: 2 }}>{erro}</Alert>}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Veículo</TableCell>
              <TableCell>Tipo</TableCell>
              <TableCell>Serviço</TableCell>
              <TableCell>Data</TableCell>
              <TableCell>Descrição</TableCell>
              <TableCell>Valor</TableCell>
              <TableCell>Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {manutencoesFiltradas.map((manut) => (
              <TableRow key={manut.id}>
                <TableCell>{manut.id}</TableCell>
                <TableCell>{manut.veiculoPlaca || veiculos.find(v => v.id === manut.veiculoId)?.placa}</TableCell>
                <TableCell>{manut.tipo}</TableCell>
                <TableCell>{manut.tipoServico}</TableCell>
                <TableCell>{manut.dataManutencao}</TableCell>
                <TableCell>{manut.descricao?.substring(0, 50)}...</TableCell>
                <TableCell>R$ {manut.valor?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</TableCell>
                <TableCell>
                  <IconButton size="small" onClick={() => handleOpen(manut)}>
                    <Edit />
                  </IconButton>
                  <IconButton size="small" color="error" onClick={() => manut.id && handleDelete(manut.id)}>
                    <Delete />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editingManutencao ? 'Editar Manutenção' : 'Nova Manutenção'}</DialogTitle>
        <DialogContent>
          {erro && <Alert severity="error" sx={{ mb: 2 }}>{erro}</Alert>}
          <form id="manutencao-form" onSubmit={handleSubmit(onSubmit as any)}>
            <Controller
              name="veiculoId"
              control={control}
              render={({ field }) => (
                <TextField
                  select
                  fullWidth
                  label="Veículo"
                  name={field.name}
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  ref={field.ref}
                  error={!!errors.veiculoId}
                  helperText={errors.veiculoId?.message}
                  margin="normal"
                >
                  {veiculos.map((v) => (
                    <MenuItem key={v.id} value={v.id}>
                      {v.placa} - {v.marca} {v.modelo}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />

            <Controller
              name="tipo"
              control={control}
              render={({ field }) => (
                <TextField
                  select
                  fullWidth
                  label="Tipo"
                  name={field.name}
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  ref={field.ref}
                  error={!!errors.tipo}
                  helperText={errors.tipo?.message}
                  margin="normal"
                >
                  <MenuItem value="PREVENTIVA">Preventiva</MenuItem>
                  <MenuItem value="CORRETIVA">Corretiva</MenuItem>
                </TextField>
              )}
            />

            <Controller
              name="tipoServico"
              control={control}
              render={({ field }) => (
                <TextField
                  select
                  fullWidth
                  label="Tipo de Serviço"
                  name={field.name}
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  ref={field.ref}
                  error={!!errors.tipoServico}
                  helperText={errors.tipoServico?.message}
                  margin="normal"
                >
                  <MenuItem value="TROCA_OLEO">Troca de Óleo</MenuItem>
                  <MenuItem value="TROCA_FILTRO_OLEO">Troca de Filtro de Óleo</MenuItem>
                  <MenuItem value="TROCA_FILTRO_AR">Troca de Filtro de Ar</MenuItem>
                  <MenuItem value="TROCA_FILTRO_COMBUSTIVEL">Troca de Filtro de Combustível</MenuItem>
                  <MenuItem value="ALINHAMENTO">Alinhamento</MenuItem>
                  <MenuItem value="BALANCEAMENTO">Balanceamento</MenuItem>
                  <MenuItem value="REVISAO_GERAL">Revisão Geral</MenuItem>
                  <MenuItem value="OUTRO">Outro</MenuItem>
                </TextField>
              )}
            />

            <Controller
              name="dataManutencao"
              control={control}
              render={({ field }) => (
                <DateMaskField
                  label="Data"
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  name={field.name}
                  inputRef={field.ref}
                  error={!!errors.dataManutencao}
                  helperText={errors.dataManutencao?.message}
                />
              )}
            />

            <TextField
              fullWidth
              label="Descrição"
              {...register('descricao')}
              error={!!errors.descricao}
              helperText={errors.descricao?.message}
              margin="normal"
              multiline
              rows={2}
            />

            <TextField
              fullWidth
              label="Peças Substituídas"
              {...register('pecasSubstituidas')}
              margin="normal"
              multiline
              rows={2}
            />

            <TextField
              fullWidth
              label="Valor"
              type="number"
              {...register('valor')}
              error={!!errors.valor}
              helperText={errors.valor?.message}
              margin="normal"
            />

            <TextField
              fullWidth
              label="Quilometragem"
              type="number"
              {...register('quilometragemNaManutencao')}
              error={!!errors.quilometragemNaManutencao}
              helperText={errors.quilometragemNaManutencao?.message}
              margin="normal"
            />

            <Controller
              name="proximaTrocaData"
              control={control}
              render={({ field }) => (
                <DateMaskField
                  label="Próxima Troca (Data)"
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  name={field.name}
                  inputRef={field.ref}
                />
              )}
            />

            <TextField
              fullWidth
              label="Próxima Troca (Quilometragem)"
              type="number"
              {...register('proximaTrocaQuilometragem')}
              margin="normal"
            />
          </form>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancelar</Button>
          <Button type="submit" form="manutencao-form" variant="contained" disabled={salvando}>
            {salvando ? 'Salvando...' : (editingManutencao ? 'Atualizar' : 'Cadastrar')}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={confirmDelete.open}
        title="Confirmar exclusão"
        message="Tem certeza que deseja excluir esta manutenção? Esta ação não pode ser desfeita."
        confirmText="Excluir"
        severity="error"
        onConfirm={confirmDeleteAction}
        onCancel={() => setConfirmDelete({ open: false, id: null })}
      />
    </Container>
  );
};

export default Manutencoes;
